import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { colours } from './playerStyles'
import { canOpen, isComplete, outlineLabel } from './screens'

const rowSx = {
  width: '100%',
  minHeight: 44,
  justifyContent: 'flex-start',
  textAlign: 'left',
  gap: 1,
  px: 1,
  py: 0.75,
  borderRadius: 1,
  fontSize: '1rem',
  lineHeight: 1.4,
  color: colours.text,
  '&:hover': { bgcolor: colours.accentSoft },
  '&.Mui-disabled': { color: colours.muted },
}

const StatusIcon = ({ status }) => {
  if (status === 'Completed') return <CheckCircleIcon fontSize="small" sx={{ color: colours.good }} />
  if (status === 'Locked') return <LockOutlinedIcon fontSize="small" sx={{ color: colours.muted }} />
  return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: colours.muted }} />
}

const moduleStatus = (entries, blocks, firstOpen) => {
  if (entries.every(({ screen }) => isComplete(blocks, screen.block.id))) return 'Completed'
  if (!firstOpen) return 'Locked'
  return entries.some(({ screen }) => blocks?.[screen.block.id]?.firstSeenAt) ? 'In progress' : 'Not started'
}

// The list of modules and their screens. A learner can jump to anything
// they have already completed (or anything at all with locks off); the rest
// is shown as locked.
const ModuleOutline = ({ modules, screens, blocks, currentIndex, locksOff, onJump }) => {
  const currentModuleId = screens[currentIndex]?.module.id
  const [expanded, setExpanded] = useState(() => new Set([currentModuleId]))

  useEffect(() => {
    setExpanded((previous) => (previous.has(currentModuleId) ? previous : new Set([...previous, currentModuleId])))
  }, [currentModuleId])

  const toggle = (moduleId) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(moduleId)) next.delete(moduleId)
      else next.add(moduleId)
      return next
    })

  return (
    <Box component="nav" id="course-outline" aria-label="Course outline">
      <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 1 }}>
        Course outline
      </Typography>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {modules.map((module, moduleIndex) => {
          const entries = screens
            .map((screen, index) => ({ screen, index }))
            .filter(({ screen }) => screen.module.id === module.id)
          if (!entries.length) return null
          const firstIndex = entries[0].index
          const firstOpen = canOpen(screens, blocks, firstIndex, locksOff)
          const status = moduleStatus(entries, blocks, firstOpen)
          const isOpen = expanded.has(module.id)
          const isCurrentModule = module.id === currentModuleId
          const panelId = `outline-module-${module.id}`

          return (
            <Box
              component="li"
              key={module.id}
              sx={{ borderTop: moduleIndex ? `1px solid ${colours.hairline}` : 'none', py: 0.5 }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                <ButtonBase
                  sx={{ ...rowSx, alignItems: 'flex-start', flex: 1, minWidth: 0 }}
                  disabled={!firstOpen}
                  onClick={() => onJump(firstIndex)}
                  aria-current={isCurrentModule ? 'true' : undefined}
                >
                  <Box sx={{ pt: '2px', display: 'flex' }}>
                    <StatusIcon status={status} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 700, overflowWrap: 'anywhere' }}>
                      {moduleIndex + 1}. {module.title}
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontSize: '0.9375rem', color: colours.muted }}>
                      About {module.estimatedMinutes} {module.estimatedMinutes === 1 ? 'minute' : 'minutes'} · {status}
                    </Box>
                  </Box>
                </ButtonBase>
                <IconButton
                  onClick={() => toggle(module.id)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} the screens in ${module.title}`}
                >
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>

              <Box component="ol" id={panelId} hidden={!isOpen} sx={{ listStyle: 'none', m: 0, p: 0, pl: 3.5 }}>
                {entries.map(({ screen, index }) => {
                  const done = isComplete(blocks, screen.block.id)
                  const open = canOpen(screens, blocks, index, locksOff)
                  const isCurrent = index === currentIndex
                  const screenStatus = done ? 'Completed' : open ? 'Not completed' : 'Locked'
                  return (
                    <li key={screen.block.id}>
                      <ButtonBase
                        sx={{
                          ...rowSx,
                          fontSize: '0.9375rem',
                          fontWeight: isCurrent ? 700 : 400,
                          bgcolor: isCurrent ? colours.accentSoft : 'transparent',
                        }}
                        disabled={!open}
                        onClick={() => onJump(index)}
                        aria-current={isCurrent ? 'step' : undefined}
                      >
                        <StatusIcon status={screenStatus} />
                        <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          {outlineLabel(screen.block)}
                          <Box component="span" sx={{ color: colours.muted, fontWeight: 400 }}>
                            {' '}
                            ({isCurrent ? `${screenStatus}, current screen` : screenStatus})
                          </Box>
                        </Box>
                      </ButtonBase>
                    </li>
                  )
                })}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default ModuleOutline
